import { useEffect, useRef } from "react";
import { useStore } from "react-redux";
import {
  filesStorageApi,
  useLazyGetFolderSyncStatusQuery,
} from "features/fileSystem/filesStorage";
import { removeFolderConnectSync } from "features/folderConnectSyncSlice";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import type { RootState } from "features/store";

export default function FolderConnectSyncPoller() {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const [triggerSyncStatus] = useLazyGetFolderSyncStatusQuery();
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  useEffect(() => {
    let stopped = false;
    let tickInFlight = false;
    const interval = setInterval(async () => {
      if (stopped || tickInFlight) return;
      tickInFlight = true;
      try {
        const syncs = store.getState().folderConnectSync.syncs;
        const folderIds = Object.keys(syncs).map(Number);
        if (folderIds.length === 0) return;

        const readyNames: string[] = [];
        const failedNames: string[] = [];

        for (const folderId of folderIds) {
          const name = syncs[folderId] ?? "";
          try {
            const res = await triggerSyncStatus(folderId).unwrap();
            if (res.sync_status === "ready") {
              dispatchRef.current(removeFolderConnectSync({ folderId }));
              readyNames.push(name || `Folder ${folderId}`);
            } else if (res.sync_status === "failed") {
              dispatchRef.current(removeFolderConnectSync({ folderId }));
              failedNames.push(name || `Folder ${folderId}`);
            }
          } catch {
          }
        }

        if (readyNames.length === 0 && failedNames.length === 0) {
          return;
        }

        dispatchRef.current(
          filesStorageApi.util.invalidateTags([
            { type: "FileSystem", id: "__root__" },
          ]),
        );

        const parts: string[] = [];
        if (readyNames.length === 1) {
          parts.push(`Folder "${readyNames[0]}" synced.`);
        } else if (readyNames.length > 1) {
          parts.push(
            `${readyNames.length} folders finished syncing: ${readyNames.map((n) => `"${n}"`).join(", ")}.`,
          );
        }
        if (failedNames.length === 1) {
          parts.push(`Folder "${failedNames[0]}" failed to sync.`);
        } else if (failedNames.length > 1) {
          parts.push(
            `${failedNames.length} folders failed to sync: ${failedNames.map((n) => `"${n}"`).join(", ")}.`,
          );
        }

        const severity: "success" | "error" | "warning" =
          failedNames.length && readyNames.length
            ? "warning"
            : failedNames.length
              ? "error"
              : "success";

        dispatchRef.current(
          pushNotification({
            message: parts.join(" "),
            severity,
          }),
        );
      } finally {
        tickInFlight = false;
      }
    }, 1000);

    return () => {
      stopped = true;
      clearInterval(interval);
    };
  }, [store, triggerSyncStatus]);

  return null;
}
